import type {Role} from "@repo/auth-shared";

import {getAge} from "./age.ts";
import type {ProfileModelType} from "./profile-model.ts";

/**
 * Determines if the birthday provided is older than 13 years.
 * The day is irrelevant for this calculation since our business rules
 * dictate that the last of the month is used. (We don't collect day of birth,
 * only month and year)
 *
 * NOTE: This function is for logged-out users. For logged-in users, it's more
 * convenient to use isConsideredOverAgeGate() or isDefinitelyOverAgeGate()
 * instead.
 *
 * @param birthday - a users birthday
 * @returns True if the birthday is over 13 years, false otherwise
 */
export function isDateOverAgeGate(birthday: Date | string): boolean {
    return getAge(birthday) >= 13;
}

/**
 * Determines if the date is over the age gate OR the user does not have the learner role.
 *
 * @param birthday - a user's birthday
 * @param role - the user's role in the system
 * @returns True if the birthday is over 13 years or user is not a learner, false otherwise
 */
export function isDateConsideredOverAgeGate(
    birthday: Date | string,
    role: Role,
): boolean {
    return isDateOverAgeGate(birthday) || role !== "learner";
}

/**
 * Helper function that takes a profile and returns a boolean indicating
 * whether the user is probably over 13.
 * - Teachers and parents are considered to probably be 13 or older.
 * - Learners without a birthdate are considered to be under 13.
 * - If you need to be more strict, and birthday is required, use
 *   `isDefinitelyOverAgeGate`.
 *
 * NOTE: because we calculate based on birthMonthYear, the learners birthday
 * is considered to be the last day of the month.
 */
export function isConsideredOverAgeGate(profile: ProfileModelType): boolean {
    if (!profile) {
        return false;
    }

    // These types of users don't have birthdates, but are considered
    // over 13.
    if (profile?.get("isParent") || profile?.get("isTeacher")) {
        return true;
    }

    return isDefinitelyOverAgeGate(profile);
}

/**
 * Helper function that takes a profile and returns a boolean indicating
 * whether the user is definitely over 13.
 * - Teachers, Parents, and Learners, without a birthdate are considered to be
 *   under 13.
 * - If you want to consider all Teachers and Parents as over 13, use
 *   `isConsideredOverAgeGate`.
 *
 * NOTE: because we calculate based on birthMonthYear, the learners birthday
 * is considered to be the last day of the month.
 */
export function isDefinitelyOverAgeGate(profile: ProfileModelType): boolean {
    if (!profile) {
        return false;
    }

    const birthMonthYear = profile.get("birthMonthYear");
    // If we don't have a birthdate, the user is considered not over 13.
    if (!birthMonthYear) {
        return false;
    }

    return isDateOverAgeGate(birthMonthYear);
}
